/**
 * 列表（Phase 3f）。
 *
 * 列表本身（名字、人数、成员）放在 `lists` 前缀下，时间线放在 `timeline` 前缀下：
 * 点赞、转发的乐观更新遍历 timeline 前缀，列表时间线里的帖子才会跟着变。
 */

import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { ApiError } from '@/api/client'
import type { ListInput } from '@/api/endpoints'
import { listsApi } from '@/api/endpoints'
import { keys } from '@/api/keys'
import type { Page, Post, User, UserList } from '@/api/types'
import { useToast } from '@/components/ui/Toast'
import { useT } from '@/i18n'

export function useMyLists(member?: string) {
  return useQuery({
    queryKey: keys.listsMine(member),
    queryFn: () => listsApi.mine(member),
    select: (data) => data.items,
  })
}

export function useList(id: string) {
  return useQuery({
    queryKey: keys.list(id),
    queryFn: () => listsApi.get(id),
    select: (data) => data.list,
    enabled: Boolean(id),
  })
}

export function useListTimeline(id: string) {
  return useInfiniteQuery({
    queryKey: keys.timelineList(id),
    queryFn: ({ pageParam }) => listsApi.timeline(id, pageParam),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last: Page<Post>) => last.next_cursor ?? undefined,
    enabled: Boolean(id),
  })
}

export function useListMembers(id: string) {
  return useInfiniteQuery({
    queryKey: keys.listMembers(id),
    queryFn: ({ pageParam }) => listsApi.members(id, pageParam),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last: Page<User>) => last.next_cursor ?? undefined,
    enabled: Boolean(id),
  })
}

/** 写完之后：详情直接换成响应里的那份，其余（我的列表、成员、时间线）作废重取 */
function useSettle() {
  const client = useQueryClient()
  return (list: UserList | null, id: string) => {
    if (list) {
      client.setQueryData(keys.list(id), { list })
      client.invalidateQueries({ queryKey: keys.timelineList(id) })
    } else {
      client.removeQueries({ queryKey: keys.list(id) })
      client.removeQueries({ queryKey: keys.listMembers(id) })
      client.removeQueries({ queryKey: keys.timelineList(id) })
    }
    client.invalidateQueries({ queryKey: keys.listsMine() })
  }
}

function useErrorToast() {
  const toast = useToast()
  const t = useT()
  return (error: unknown) =>
    toast.error(error instanceof ApiError ? error.message : t('toast.actionFailed'))
}

export function useCreateList() {
  const settle = useSettle()
  return useMutation({
    mutationFn: (input: ListInput) => listsApi.create(input),
    onSuccess: ({ list }) => settle(list, list.id),
  })
}

export function useUpdateList(id: string) {
  const settle = useSettle()
  return useMutation({
    mutationFn: (patch: Partial<ListInput>) => listsApi.update(id, patch),
    onSuccess: ({ list }) => settle(list, id),
  })
}

export function useDeleteList() {
  const settle = useSettle()
  const onError = useErrorToast()
  return useMutation({
    mutationFn: (id: string) => listsApi.remove(id),
    onSuccess: (_data, id) => settle(null, id),
    onError,
  })
}

/** 加进 / 移出列表。`member` 是当前状态：true 就移出 */
export function useListMembership() {
  const settle = useSettle()
  const client = useQueryClient()
  const onError = useErrorToast()
  return useMutation({
    mutationFn: ({
      listId,
      username,
      member,
    }: {
      listId: string
      username: string
      member: boolean
    }) => (member ? listsApi.removeMember(listId, username) : listsApi.addMember(listId, username)),
    onSuccess: ({ list }) => {
      settle(list, list.id)
      client.invalidateQueries({ queryKey: keys.listMembers(list.id) })
    },
    onError,
  })
}
